import React, { useCallback, useEffect, useState, useMemo, useRef } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import moment from 'moment';
import { useDispatch, useSelector, shallowEqual } from 'react-redux';
import { isEmpty, isNull, orderBy } from 'lodash';
import * as roomActions from 'features/room-builder/duck';
import * as tcEventActions from 'features/tc-event-dashboard/duck';
import * as taskActions from 'features/task-management/duck';
import * as onlineTestingActions from '../duck';
import { ONLINE_PROVIDERS, ONLINE_PSI_REGS_FILTER, ROOM_STATUS } from 'shared/constants';
import { getUpcomingTasks } from 'features/task-management/selectors';
import { RoomPolling } from 'features/task-management';
import OnlineTestingService from '../OnlineTestingService';
import {
  Tabs,
  Tab,
  AlertNotice,
  RefreshButton,
  CustomFilters,
  CheckboxList,
  ScrollFade,
} from 'shared/components';
import {
  GridView,
  ListView,
  NationalStudentGridCell,
  AuthorizationTickets,
  TaskSection,
  RoomCode,
} from '../index';
import { mapOnlinePsiRegs } from '../selectors';

import './OnlineTestingDashboard.scss';

const TABS = {
  listView: 1,
  gridView: 2,
};

const RenderCountBox = (
  setIsUpdateStatus,
  autoRefreshUpdateStatus,
  roomStatus,
  allStudentsReady,
  allEligibleStudents,
  allStudentsReadyIds,
  allSeatedStudents,
  onlinePsiRegsValues,
  t
) => {
  const handleStartClick = async () => {
    const roomStartedResults = await OnlineTestingService.putPsiStudentStatus('start', {
      regIDs: allStudentsReadyIds,
    });
    if (roomStartedResults) {
      setIsUpdateStatus(true);
      autoRefreshUpdateStatus();
    }
  };

  return (
    <div className={`border-box-radius details-bar `}>
      {allSeatedStudents && (
        <span className="details-bar-first-item">
          <b>{`${t('onlineTesting.gridView.seatedLabel')} : ${allSeatedStudents.length}`}</b> /{' '}
          {onlinePsiRegsValues.length}
        </span>
      )}
      <span>
        <b>{`${t('onlineTesting.ready')} : ${allStudentsReady.length}`}</b> /{' '}
        {allEligibleStudents.length}
      </span>
      <button
        className="btn btn-primary btn-action btn-small"
        onClick={handleStartClick}
        disabled={
          !(
            allStudentsReady.length &&
            allEligibleStudents.length &&
            allStudentsReady.length === allEligibleStudents.length &&
            roomStatus.status === ROOM_STATUS.opened
          )
        }>
        {t('common.labels.start')}
      </button>
    </div>
  );
};

const getCheckboxList = () => {
  const checkboxList = [];
  Object.entries(ONLINE_PSI_REGS_FILTER).forEach(([key, value], i) => {
    checkboxList.push({ value: key, label: value, checked: false, id: i, key: i });
  });
  return checkboxList;
};

const NationalOnlineTestingDashboard = () => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { roomId, eventId } = useParams();

  let poller;
  const checkboxList = getCheckboxList();
  const [seatedExaminees, setSeatedExaminees] = useState([]);
  const [lastFetchTime, setLastFetchTime] = useState(null);
  const [isUpdateStatus, setIsUpdateStatus] = useState(false);
  const [isAutoRefreshStarted, setIsAutoRefreshStarted] = useState(false);
  const isAutoRefreshStartedRef = useRef(false);
  const [selectedTab, setSelectedTab] = useState(TABS.listView);
  const [filterCheckboxes, setFilterCheckboxes] = useState(checkboxList);
  const [listOfSelectedExaminees, setListOfSelectedExaminees] = useState([]);
  const [windowSize, setWindowSize] = useState(window.innerWidth);
  const [scrollPosition, setScrollPosition] = useState(0);
  const [tableScrollPosition, setTableScrollPosition] = useState(0);
  const [onlinePsiRegsValues, setOnlinePsiRegsValues] = useState([]);

  const {
    room,
    onlinePsiRegs,
    examineesMappedToSeats,
    loadingPsiRegs,
    upcomingTasks,
    tcEvent,
    roomStatus,
  } = useSelector(
    (state) => ({
      room: state.roomOptions.room,
      onlinePsiRegs: state.onlineTesting.onlinePsiRegs,
      examineesMappedToSeats: mapOnlinePsiRegs(state),
      loadingPsiRegs: state.onlineTesting.loadingPsiRegs,
      upcomingTasks: getUpcomingTasks(state),
      tcEvent: state.tcEvent.currentEvent,
      roomStatus: state.taskManagement.roomStatus,
    }),
    shallowEqual
  );

  const allEligibleStudents =
    onlinePsiRegsValues?.length &&
    onlinePsiRegsValues.filter(
      (reg) => reg.status !== 'marked_complete' && reg.status !== 'complete'
    );
  const allStudentsReady =
    onlinePsiRegsValues?.length && onlinePsiRegsValues.filter((reg) => reg.status === 'ready');
  const allStudentsReadyIds =
    allStudentsReady.length && allStudentsReady.map((student) => student.regId);
  const allSeatedStudents =
    (onlinePsiRegsValues?.length &&
      onlinePsiRegsValues.filter((reg) => (reg.seatCol || reg.seatRow) !== null)) ||
    [];

  const noFilterChecked = useMemo(
    () => filterCheckboxes.every((box) => !box.checked),
    [filterCheckboxes]
  );

  const filteredRegs = useMemo(
    () =>
      noFilterChecked
        ? orderBy(onlinePsiRegsValues, 'lastName')
        : orderBy(
            filterCheckboxes
              .filter((box) => box.checked)
              .flatMap((box) =>
                onlinePsiRegsValues.filter((reg) => box.value === String(reg.status))
              ),
            'lastName'
          ),
    [noFilterChecked, onlinePsiRegsValues, filterCheckboxes]
  );

  const isPsiEvent = useMemo(
    () =>
      !isEmpty(tcEvent) &&
      tcEvent?.testingEvent?.adminId?.onlineProvider === ONLINE_PROVIDERS.psi &&
      room.roomType?.onlineFlag,
    [room, tcEvent]
  );

  const fetchData = useCallback(
    async (activeTab) => {
      await dispatch(onlineTestingActions.getOnlinePsiRegs(roomId));

      if (activeTab) {
        setSelectedTab(activeTab);
        if (activeTab === TABS.gridView) {
          await dispatch(roomActions.getRoom(roomId));
        }
      } else if (selectedTab === TABS.gridView) {
        await dispatch(roomActions.getRoom(roomId));
      }
      setLastFetchTime(moment().format('hh:mm A'));
    },
    [dispatch, roomId, selectedTab]
  );

  const autoRefreshUpdateStatus = useCallback(() => {
    const customTimer = setInterval(async () => {
      isAutoRefreshStartedRef.current = true;
      setIsAutoRefreshStarted(true);
      await fetchData();
      setIsAutoRefreshStarted(false);
      isAutoRefreshStartedRef.current = false;
    }, 1000);
    // after 10 seconds stop
    setTimeout(() => {
      isAutoRefreshStartedRef.current = true;
      setIsAutoRefreshStarted(true);
      setIsUpdateStatus(false);
      clearInterval(customTimer);
    }, 10000);
  }, [fetchData]);

  const isLoading = useMemo(() => {
    if (isUpdateStatus || isAutoRefreshStarted || isAutoRefreshStartedRef.current) {
      return false;
    }
    return loadingPsiRegs;
  }, [isAutoRefreshStarted, isUpdateStatus, loadingPsiRegs]);

  useEffect(() => {
    if (!examineesMappedToSeats.length || loadingPsiRegs) return;
    setSeatedExaminees(
      examineesMappedToSeats.flat().filter((ex) => !isNull(ex.seatCol) && !isNull(ex.seatRow))
    );
  }, [examineesMappedToSeats, loadingPsiRegs]);

  useEffect(() => {
    if (loadingPsiRegs) return;
    setOnlinePsiRegsValues(onlinePsiRegs);
  }, [loadingPsiRegs, onlinePsiRegs]);

  useEffect(() => {
    if (loadingPsiRegs || !onlinePsiRegsValues.length) return;
    setSeatedExaminees(
      onlinePsiRegsValues.filter((ex) => !isNull(ex.seatCol) && !isNull(ex.seatRow))
    );
    setListOfSelectedExaminees((prev) =>
      onlinePsiRegsValues.filter((reg) => prev.some((selected) => selected.id === reg.id))
    );
  }, [loadingPsiRegs, onlinePsiRegsValues]);

  useEffect(() => {
    if (isUpdateStatus) {
      autoRefreshUpdateStatus();
      sessionStorage.setItem('isUpdateStatus', true);
    } else {
      sessionStorage.removeItem('isUpdateStatus');
    }
  }, [autoRefreshUpdateStatus, isUpdateStatus]);

  useEffect(() => {
    const handleResize = () => {
      setWindowSize(window.innerWidth);
    };

    window.addEventListener('resize', handleResize);

    dispatch(roomActions.getRoom(roomId));
    dispatch(onlineTestingActions.getOnlinePsiRegs(roomId));
    dispatch(taskActions.getRoomStatus(roomId));
    dispatch(taskActions.getRoomTimers(roomId));

    if (isEmpty(tcEvent)) {
      dispatch(tcEventActions.getTcEvent(eventId)).then((data) => {
        if (!isEmpty(data)) {
          const cycleId = data.testingEvent.adminId.cycleId.id;
          dispatch(taskActions.getTasks(cycleId));
        }
      });
    } else {
      const cycleId = tcEvent.testingEvent.adminId.cycleId.id;
      dispatch(taskActions.getTasks(cycleId));
    }

    if (!poller) {
      poller = new RoomPolling({
        action: () => dispatch(taskActions.getRoomStatus(roomId)),
      });
    }

    return () => {
      if (poller) {
        poller.stop();
        poller = null;
      }
      window.removeEventListener('resize', handleResize);
    };
  }, []);

  const clearCheckboxes = useCallback(() => {
    setFilterCheckboxes((prev) => prev.map((box) => ({ ...box, checked: false })));
  }, []);

  const onCheck = useCallback((e) => {
    setFilterCheckboxes((prev) =>
      prev.map((box) => ({ ...box, checked: box.id === Number(e.target.name) && e.target.checked }))
    );
  }, []);

  const onClear = useCallback(() => {
    clearCheckboxes();
    setScrollPosition(0);
  }, [clearCheckboxes]);

  const goBackToPrevPage = () => {
    navigate(-1);
  };

  const getLeftSection = () => (
    <div className="left-section">
      {windowSize > 1199 && (
        <>
          <h4>{t('onlineTesting.title')}</h4>
          <div className="fw-bold padding-b-12">{room.name}</div>
        </>
      )}
      {roomStatus.status === ROOM_STATUS.opened && (
        <div className="task-timer-section">
          <TaskSection
            upcomingTasks={upcomingTasks}
            roomId={roomId}
            taskActions={taskActions}
            allEligibleStudents={allEligibleStudents}
            isPsiEvent
            setIsUpdateStatus={setIsUpdateStatus}
            setIsAutoRefreshStarted={setIsAutoRefreshStarted}
            isAutoRefreshStartedRef={isAutoRefreshStartedRef}
          />
          {!upcomingTasks.length && (
            <AlertNotice
              message={t('onlineTesting.tasks.infoMessage')}
              onClick={() => navigate(`/test-event/${eventId}/room/${roomId}`)}
            />
          )}
        </div>
      )}
      <RoomCode
        code={room.code}
        roomStatus={roomStatus.status}
        testCenterCode={isPsiEvent ? roomStatus.testCenterCode : ''}
      />
      {selectedTab === TABS.listView && onlinePsiRegsValues.length > 0 && (
        <ScrollFade
          setScrollPosition={setScrollPosition}
          scrollPosition={scrollPosition}
          filterCheckboxes={filterCheckboxes}>
          <CustomFilters
            title={t('onlineTesting.listView.filters')}
            clear={noFilterChecked ? '' : t('onlineTesting.listView.clear')}
            onClear={onClear}
            subTitle={t('onlineTesting.listView.show')}>
            <CheckboxList onChange={onCheck} checkboxes={filterCheckboxes} />
          </CustomFilters>
        </ScrollFade>
      )}
      {windowSize > 1199 && (
        <button className="btn btn-outline btn-small m-0 w-100" onClick={goBackToPrevPage}>
          {t('common.labels.back')}
        </button>
      )}
    </div>
  );

  return (
    <div className="online-testing-container national-container">
      {windowSize <= 1199 && (
        <>
          <h4>{t('onlineTesting.title')}</h4>
          <div className="fw-bold padding-b-12">{room.name}</div>
        </>
      )}
      {windowSize > 1199 && getLeftSection()}
      <div className="right-section">
        <div>
          <div>
            <Tabs
              defaultTab={TABS.listView}
              onClickAction={fetchData}
              loadingData={isLoading}
              rightElement={
                <div className="d-flex refresh">
                  <RefreshButton
                    fetchData={fetchData}
                    lastFetchTime={lastFetchTime}
                    autoRefresh={true}
                    setIsAutoRefreshStarted={setIsAutoRefreshStarted}
                    isAutoRefreshStartedRef={isAutoRefreshStartedRef}
                  />
                  {onlinePsiRegsValues.length > 0 && (
                    <AuthorizationTickets
                      listOfExaminees={onlinePsiRegsValues}
                      testCenterEventId={tcEvent?.id}
                    />
                  )}
                </div>
              }
              extraContent={windowSize <= 1199 && getLeftSection()}>
              <Tab title={t('onlineTesting.tabs.listView')}>
                {onlinePsiRegsValues.length > 0 ? (
                  <>
                    {RenderCountBox(
                      setIsUpdateStatus,
                      autoRefreshUpdateStatus,
                      roomStatus,
                      allStudentsReady,
                      allEligibleStudents,
                      allStudentsReadyIds,
                      allSeatedStudents,
                      onlinePsiRegsValues,
                      t
                    )}

                    {filteredRegs.length > 0 ? (
                      <ListView
                        rowHeaders={['name', 'seat', 'test', 'status']}
                        onlineRegs={filteredRegs}
                        listOfSelectedExaminees={listOfSelectedExaminees}
                        updateExamineesList={setListOfSelectedExaminees}
                        scrollPosition={tableScrollPosition}
                        setScrollPosition={setTableScrollPosition}
                        isPsiEvent
                        setLastFetchTime={setLastFetchTime}
                        setIsUpdateStatus={setIsUpdateStatus}
                        seatingChartRequired={
                          tcEvent?.testingEvent?.adminId?.cycleId?.seatingChartRequiredFlag
                        }
                      />
                    ) : (
                      <div>
                        <div className="no-results">{t('onlineTesting.listView.noResults')}</div>
                        <button
                          className="btn btn-outline btn-small no-results-button"
                          onClick={onClear}>
                          {t('onlineTesting.listView.clearFilters')}
                        </button>
                      </div>
                    )}
                  </>
                ) : (
                  <AlertNotice message={t('onlineTesting.listView.emptyStateMessage')} />
                )}
              </Tab>
              <Tab title={t('onlineTesting.tabs.gridView')}>
                {seatedExaminees.length ? (
                  <React.Fragment>
                    {RenderCountBox(
                      setIsUpdateStatus,
                      autoRefreshUpdateStatus,
                      roomStatus,
                      allStudentsReady,
                      allEligibleStudents,
                      allStudentsReadyIds,
                      allSeatedStudents,
                      onlinePsiRegsValues,
                      t
                    )}
                    <GridView
                      rows={room.seatingRowCount}
                      cols={room.seatingColumnCount}
                      cellComponent={NationalStudentGridCell}
                      seatsData={examineesMappedToSeats}
                      windowSize={windowSize}
                      setLastFetchTime={setLastFetchTime}
                      setIsUpdateStatus={setIsUpdateStatus}
                      isAutoRefreshStarted={isAutoRefreshStarted}
                      isUpdateStatus={isUpdateStatus}
                    />
                  </React.Fragment>
                ) : (
                  <AlertNotice
                    message={t('onlineTesting.gridView.emptyState')}
                    link={`/test-event/${eventId}/room/${roomId}/seating-chart`}
                    withLink
                  />
                )}
              </Tab>
            </Tabs>
          </div>
        </div>
      </div>
      {windowSize <= 1199 && (
        <div className="button-container">
          <button className="btn btn-outline btn-small m-0 w-100" onClick={goBackToPrevPage}>
            {t('common.labels.back')}
          </button>
        </div>
      )}
    </div>
  );
};

export default NationalOnlineTestingDashboard;

NationalOnlineTestingDashboard.propTypes = {};
